import { UIAvatar, UITextSmMedium, UITextXsRegular, getColorFromString } from '@gohighlevel/ghl-ui'
import { VNodeChild, h } from 'vue'
import {
  DOCUMENT_SENDER_ID,
  ELEMENTS_META,
  ELEMENTS_STYLES,
  GroupFields,
  GroupFieldsOptions,
  ICustomFieldsLink,
  ICustomFieldsLinkageForm,
  IElement,
  IRecipient,
  ITax,
  ObjectForLinkedField,
  RecipientEntityTypeEnum,
  RecipientRolesEnum,
} from '../index'
import { PAGE_SECTIONS } from '../const'
import { GROUP_LOOKUP } from '../const/groups'
import { floatingElementsSet } from './flatChildrens'
import { getNextIdForGroup } from './fieldCounter'
import type { IPage } from '../types'
import { resolvePageIdForElementInPages } from './elementTree'

import i18n from '@gohighlevel/ghl-proposals-common/src/locales'
import { getInitials } from '@gohighlevel/ghl-ui/src/utils/get-initials'
import { cloneDeep } from 'lodash'
import { v4 as uuidv4 } from 'uuid'
import { useTypography } from '../composition'

const enablePrimaryCompany = false

export const renderLabelAvatarOnSelect = (option: IRecipient & { label: string; value: string }): VNodeChild => {
  const { toCapitalize } = useTypography()
  const colorString = toCapitalize(option?.contactName || `${option.firstName} ${option.lastName}` || '').trim()
  return h(
    'div',
    {
      class: 'py-2',
      style: {
        display: 'flex',
        alignItems: 'center',
        fontWeight: 400,
        fontSize: '14px',
        lineHeight: '14px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        width: '100%',
      },
    },
    [
      h('div', { class: 'flex items-start' }, [
        h(
          UIAvatar,
          {
            round: true,
            size: 24,
            style: {
              backgroundColor: getColorFromString(colorString as string),
              fontSize: '10px',
              flex: 'none',
            },
          },
          {
            default: () => getInitials(option.label as string),
          }
        ),
        h(
          'div',
          {
            style: {
              marginLeft: '8px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          },
          [
            h(
              UITextSmMedium,
              {},
              {
                default: () => option.label as string,
              }
            ),
            h(
              UITextXsRegular,
              { class: 'text-gray-400' },
              {
                default: () => `${option.email}${option.phone ? ', ' + option.phone : ''}`,
              }
            ),
          ]
        ),
      ]),
    ]
  )
}

export const transformMenuOption = (arr: any) => {
  if (!arr) return null
  return arr.map(({ text, menu, value = text }: any) => ({
    label: text,
    value: value,
    children: menu ? transformMenuOption(menu) : undefined,
  }))
}
export const onChangeMarginPadding = (updateStyles: (key: string, value: any, id?: string, type?: string) => void) => {
  return (args: { changed: { margin: any; padding: any } }) => {
    const { margin, padding } = args.changed
    if (margin) {
      if ('top' in margin) updateStyles(ELEMENTS_STYLES.MARGIN_TOP, margin.top)
      if ('bottom' in margin) updateStyles(ELEMENTS_STYLES.MARGIN_BOTTOM, margin.bottom)
      if ('left' in margin) updateStyles(ELEMENTS_STYLES.MARGIN_LEFT, margin.left)
      if ('right' in margin) updateStyles(ELEMENTS_STYLES.MARGIN_RIGHT, margin.right)
    }

    if (padding) {
      if ('top' in padding) updateStyles(ELEMENTS_STYLES.PADDING_TOP, padding.top)
      if ('bottom' in padding) updateStyles(ELEMENTS_STYLES.PADDING_BOTTOM, padding.bottom)
      if ('left' in padding) updateStyles(ELEMENTS_STYLES.PADDING_LEFT, padding.left)
      if ('right' in padding) updateStyles(ELEMENTS_STYLES.PADDING_RIGHT, padding.right)
    }
  }
}
export function isValidHandleBars(value: string) {
  const regex = /((^{{|[^{\n\r]{{)(?!{))(?!( ?(\w\.?)+ ?(}}(?!}))))|((^{{{|[^{\n\r]{{{)(?!{))(?!( ?(\w\.?)+ ?(}}}(?!}))))|{{4,}|^{(?= )/m

  return !regex.test(value) ? true : false
}

export function validateHandlebarVariableExistInCustomValues(
  value: string,
  staticCustomValues: Record<string, any>[],
  customValLoc: Record<string, any>[],
  customFieldLoc: Record<string, any>[],
  documentVariables: Record<string, any>[]
) {
  let customValues: any = []
  const docVarOptions = transformDocumentVariables(documentVariables)
  const options = getAllCustomValLocation(customValLoc)
  const cfOptions = [getAllCustomFieldLocation(customFieldLoc)]
  const businessOptions = enablePrimaryCompany ? [getBusinessCustomFieldLocation(customFieldLoc)] : ([] as any)

  if (options && options.length > 0) {
    customValues = [...staticCustomValues, ...options, ...docVarOptions, ...cfOptions, ...businessOptions]
  }

  const children = fetchAllCustomValueChild(customValues)
  const map: any = {}
  const errorCustomValues: any = []
  children.forEach((elem: { label: string; value: string }) => {
    map[elem.value.replace(/{{/g, '').replace(/}}/g, '').trim().toString()] = elem.label
  })

  const hbKeys: string[] | null = value.match(/{{\s*(\S+?)\s*}}/g)

  if (hbKeys) {
    for (const key of hbKeys) {
      const eachKey = key.replace(/{{/g, '').replace(/}}/g, '').trim()
      if (!(eachKey in map)) {
        errorCustomValues.push(eachKey)
      }
    }
  }

  return errorCustomValues
}

export const fetchAllCustomValueChild = (customVal: any[]) => {
  return customVal.reduce((result, item) => {
    const { children, ...rest } = item
    result.push(rest)
    if (children) {
      result.push(...fetchAllCustomValueChild(children))
    }
    return result
  }, [])
}

export const getAllCustomValLocation = (customVal: Record<string, any>[]) => {
  const options = []
  options.push({
    label: 'Custom Values',
    value: 'Custom Values',
    children: customVal,
  })
  return options
}

export const getAllCustomFieldLocation = (customFields: Record<string, any>[]) => {
  const customFieldsWithoutBusiness = enablePrimaryCompany ? customFields.filter((field: any) => field?.label !== 'Company Info') : null
  const cf = {
    label: 'Custom Fields',
    value: 'Custom Fields',
    children: customFieldsWithoutBusiness || customFields,
  }
  return cf
}

export const getBusinessCustomFieldLocation = (customFields: Record<string, any>[]) => {
  const customFieldsBusiness = customFields.filter((field: any) => field?.label === 'Company Info')
  const bcf = {
    label: 'Primary Company',
    value: 'Primary Company',
    children: customFieldsBusiness?.[0]?.children,
  }
  return bcf
}

export const transformDocumentVariables = (docVar: Record<string, any>[]) => {
  const options = []
  options.push({
    label: 'Document Variables',
    value: 'Document Variables',
    children: docVar?.map(({ fieldKey }) => ({
      label: fieldKey,
      value: `{{${fieldKey}}}`,
    })),
  })
  return options
}

export const convertToRecipientOptions = (list: IRecipient[]) => {
  return (
    list.map((contact: IRecipient) => ({
      ...contact,
      label: contact?.contactName || `${contact.firstName} ${contact.lastName}`,
      value: contact.id,
    })) || []
  )
}

export const getRecipientParamsFromContact = (
  contact: any,
  entityName: RecipientEntityTypeEnum = RecipientEntityTypeEnum.CONTACTS,
  isPrimary = false
) => {
  // Handle undefined or null contact
  if (!contact) {
    return {
      id: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      country: '',
      isPrimary,
      contactName: null,
      hasCompleted: false,
      signingOrder: 1,
      shareLink: '',
      imgUrl: '',
      entityName,
      role: RecipientRolesEnum.USER,
    }
  }

  const { id, email, firstName, lastName, phone, country, contactName, phoneNumber } = contact

  const contactDetails = {
    id,
    firstName,
    lastName: lastName || '',
    email,
    phone: phoneNumber || phone,
    country,
    isPrimary,
    contactName: contactName || null,
    hasCompleted: false,
    signingOrder: 1,
    shareLink: '',
    imgUrl: '',
    entityName,
    role: RecipientRolesEnum.USER,
  }
  return contactDetails
}

/**
 * Sets appropriate properties required for a normal recipient to become the primary signer
 * @param recipient
 * @returns
 */
export const upgradeRecipientToPrimarySigner = (recipient: IRecipient) => {
  return {
    ...recipient,
    role: RecipientRolesEnum.SIGNER,
    isPrimary: true,
  }
}

/**
 * Returns recipients who are dynamically to be added in the recipient list
 * eg: elems marked as sender require the current loggedin user to be as the recipient
 * @param recipients
 * @param fillableElements
 * @param userData
 * @returns
 */
export const getAdditionalRecipientsWithRoles = (recipients: IRecipient[], fillableElements: IElement[], userData: any) => {
  if (!fillableElements) return []
  // Return empty array if userData is not available
  if (!userData) return []

  const currentUser = getRecipientParamsFromContact(userData, RecipientEntityTypeEnum.USERS, false)
  const senderUserExists = fillableElements.some(elem => {
    return elem.component?.options?.recipient === DOCUMENT_SENDER_ID
  })
  if (senderUserExists) {
    const contactDetails = {
      ...currentUser,
      contactName: `${currentUser.firstName} ${currentUser.lastName}`,
    }

    if (!recipients.find(elem => elem.id === contactDetails.id)) {
      return [contactDetails]
    }
  }
  return []
}

export const removeUserFromRecipientIfElementIsNotAssigned = (signatureElements: any[], recipients: any[]) => {
  const signatureUserIds = signatureElements.reduce((acc, signature) => {
    if (
      signature.component?.options[ELEMENTS_META.RECIPIENT] &&
      signature.component?.options[ELEMENTS_META.ENTITY_NAME] === RecipientEntityTypeEnum.USERS
    ) {
      acc.push(`${signature.component.options[ELEMENTS_META.RECIPIENT]}_${signature.component.options[ELEMENTS_META.ENTITY_NAME]}`)
    }
    return acc
  }, [] as string[])
  const recipientList = recipients.filter(elem => {
    return (
      elem.entityName === RecipientEntityTypeEnum.CONTACTS ||
      (elem.entityName === RecipientEntityTypeEnum.USERS && signatureUserIds.indexOf(`${elem.id}_${elem.entityName}`) > -1) ||
      (elem.entityName === RecipientEntityTypeEnum.USERS && elem.id === DOCUMENT_SENDER_ID)
    )
  })
  const recipientWithRolesList = updateRecipientRolesIfElementIsAssigned(signatureUserIds, recipientList)
  return recipientWithRolesList
}

export const getUserIdsFromSignElements = (signatureElements: any[]) => {
  const signatureUserIds = signatureElements.reduce((acc, signature) => {
    if (signature.component?.options[ELEMENTS_META.RECIPIENT] && signature.component?.options[ELEMENTS_META.ENTITY_NAME]) {
      acc.push(`${signature.component.options[ELEMENTS_META.RECIPIENT]}_${signature.component.options[ELEMENTS_META.ENTITY_NAME]}`)
    }
    return acc
  }, [] as string[])

  return signatureUserIds
}

export const updateRecipientRolesIfElementIsAssigned = (signatureElements: any[], recipients: any[]) => {
  const signatureUserIds = getUserIdsFromSignElements(signatureElements)
  const recipientList = recipients.map(recipient => {
    return signatureUserIds.indexOf(`${recipient.id}_${recipient.entityName}`) > -1
      ? { ...recipient, role: RecipientRolesEnum.SIGNER }
      : { ...recipient, role: RecipientRolesEnum.USER }
  })
  return recipientList
}

export const fetchPrimaryRecipient = (recipients: IRecipient[]) => {
  if (recipients && recipients.length > 0) {
    return recipients.find(elem => {
      return elem.isPrimary === true
    })
  }
  return null
}

export const recipientThumbnail = (firstName = '', lastName = '') => {
  const initials = getInitials(`${firstName} ${lastName}`)
  return initials.length > 0 ? initials : ''
}

export const getMarginProperties = (element?: IElement) => {
  return {
    top: element?.responsiveStyles?.large?.marginTop || '0px',
    bottom: element?.responsiveStyles?.large?.marginBottom || '0px',
    right: element?.responsiveStyles?.large?.marginRight || '0px',
    left: element?.responsiveStyles?.large?.marginLeft || '0px',
  }
}

export const getPaddingProperties = (element?: IElement) => {
  return {
    top: element?.responsiveStyles?.large?.paddingTop || '0px',
    bottom: element?.responsiveStyles?.large?.paddingBottom || '0px',
    right: element?.responsiveStyles?.large?.paddingRight || '0px',
    left: element?.responsiveStyles?.large?.paddingLeft || '0px',
  }
}

export const extractRecipientIdAndEntityName = (val: string) => {
  const recipientData = val.split('_')
  if (recipientData.length === 2) {
    const recipientId = recipientData[0]
    const entityName = recipientData[1]
    return { recipientId, entityName }
  }
  return { recipientId: null, entityName: null }
}

export const getTaxItemName = (tax: ITax, taxInclusive: boolean) => {
  if (taxInclusive) return `${tax.name} (${tax.rate}% ${i18n.global.t('common.includedInPrices')})`
  return tax.name
}

export const removeFillableElementsWhichIsPartOfGroup = (allFillableElements: IElement[], groups: GroupFields<GroupFieldsOptions>[]) => {
  const groupElementsList = groups.reduce((acc, g) => {
    g.children?.forEach(element => {
      return acc.push(element.elementId)
    })
    return acc
  }, [] as any[])

  const allFillableElementsNotPartOfGroups = allFillableElements.filter(element => !groupElementsList.includes(element.id))
  return allFillableElementsNotPartOfGroups
}

export const removeOpportunityCustomFields = (customFieldOptions: {
  label: string
  value: string
  children: {
    children: { label: string; value: string }[]
    label: string
    value: string
  }[]
}) => {
  if (customFieldOptions && customFieldOptions.children && customFieldOptions.children.length > 0) {
    const filedOptions = customFieldOptions?.children?.filter(item => {
      const hasOpportunityChild = item.children.some(child => child.value.startsWith('{{ opportunity.'))
      return !hasOpportunityChild
    })
    customFieldOptions['children'] = filedOptions
    return customFieldOptions
  } else {
    return customFieldOptions
  }
}

export const generateCustomLinkItemPayload = (formValue: ICustomFieldsLinkageForm) => {
  const fieldKey = formValue.objectName === ObjectForLinkedField.DOCUMENT_VARIABLE ? formValue.documentFieldKey : formValue.customFieldKey

  return {
    id: formValue.customFieldId || '',
    fieldKey: fieldKey || '',
    value: '',
    dataType: formValue.customFieldType || '',
    objectType: formValue.objectName || '',
    category: formValue.customFieldCategory || '',
    fieldLabel: formValue.customFieldLabel || '',
  }
}

export function escape(input: string, withExt = true): string {
  const fileNameRegex = /[^A-Za-z0-9\-_.]/ // Special characters allowed in a filename

  const [fileName, fileExtension] = input.split('.')

  if (fileNameRegex.test(fileName)) {
    const escapedFileName = fileName.replace(/[^A-Za-z0-9\-_.]/g, function (char: string): string {
      return '%' + char.charCodeAt(0).toString(16).toUpperCase()
    })

    if (withExt) {
      return `${escapedFileName}.${fileExtension}`
    }
    return escapedFileName
  }

  return input
}

export const getFileName = (fileName: string, documentId: string) => {
  return escape((fileName && fileName.replace(/\\/g, '').replace(/\s/g, '_').replace(/\//g, '_')) || `document_${documentId}`, false)
}

export async function downloadCSV(csv: string, documentName: string, documentId: string) {
  try {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${getFileName(documentName, documentId)}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Error downloading the CSV:', error)
  }
}

export function splitAtFirstColon(input: string): [string, string] {
  const index = input.indexOf(':')
  if (index === -1) {
    return [input, '']
  }
  return [input.slice(0, index), input.slice(index + 1)]
}

export function isValidUrl(url: string): boolean {
  const urlRegex = /^(https?:\/\/)([\w-]+(\.[\w-]+)+)([/?#]\S*)?$/i
  return urlRegex.test(url)
}

/**
 * Deep-clones a tree (or array of trees) and regenerates every `id`.
 *
 * @param input  Either a single TreeNode or an array of them
 * @returns      A fresh copy with all `id` fields replaced
 */

export function cloneWithNewIds<T extends IElement<any>>(input: T | T[]): { data: T | T[]; idMap: Record<string, string> } {
  // 1) clone the whole thing so we never mutate the original
  const data = cloneDeep(input)
  const idMap: Record<string, string> = {}

  // 2) helper: walk a list of nodes
  function traverse(nodes: IElement[]) {
    for (const node of nodes) {
      if (node.id != null) {
        const oldId = node.id
        const newId = uuidv4()
        node.id = newId
        idMap[oldId] = newId
      }
      if (Array.isArray(node.children) && node.children.length) {
        traverse(node.children)
      }
    }
  }

  // 3) kick off traversal
  if (Array.isArray(data)) {
    traverse(data)
  } else {
    traverse([data])
  }

  return { data, idMap }
}

export const cloneGroupWithNewIds = (groups: GroupFields<GroupFieldsOptions>[], idMap: Record<string, string>) => {
  const data = cloneDeep(groups)
  return data.map(group => {
    return {
      ...group,
      id: uuidv4(),
      children: group.children.map(child => {
        return {
          ...child,
          elementId: idMap[child.elementId],
        }
      }),
    }
  })
}

/**
 * Map type: sourceElementId -> { targetPageId -> cloneElementId }.
 * Used so that when header/footer are cloned to multiple pages, fillable
 * elements get stable per-page clone IDs (reused when replicating again or after load).
 */
export type HeaderFooterCloneIdMap = Record<string, Record<string, string>>

/** All element ids under a page section's `children` tree (not the section root id). */
export function collectElementIdsInSectionSubtree(section: IElement | undefined | null): Set<string> {
  const out = new Set<string>()
  const walk = (nodes: IElement[] | undefined) => {
    if (!nodes?.length) return
    for (const n of nodes) {
      if (!n) continue
      if (n.id != null) out.add(n.id)
      walk(n.children)
    }
  }
  walk(section?.children)
  return out
}

/** Checkbox groups whose members all sit on `hostPageId` and inside the given section subtree. */
export function findCheckboxGroupsWhollyOnHostInSection(
  groups: GroupFields<GroupFieldsOptions>[],
  hostPageId: string,
  sectionElementIds: Set<string>,
  pages: IPage[]
): GroupFields<GroupFieldsOptions>[] {
  return groups.filter(g => {
    if (g.component?.options?.groupType !== GROUP_LOOKUP.CHECKBOX_GROUP) return false
    if (!g.children?.length || g.children.length < 2) return false
    if (
      !g.children.every(c => {
        const pid = c.pageId ?? resolvePageIdForElementInPages(pages, c.elementId)
        return pid === hostPageId
      })
    ) {
      return false
    }
    return g.children.every(c => sectionElementIds.has(c.elementId))
  })
}

/**
 * If `group` is a checkbox group whose members all lie on one page and inside that page's header or footer
 * subtree, returns that page and section. Otherwise null (body groups or invalid).
 */
export function getHeaderOrFooterHostForWhollyContainedCheckboxGroup(
  group: GroupFields<GroupFieldsOptions>,
  pages: IPage[]
): { pageId: string; sectionType: typeof PAGE_SECTIONS.HEADERS | typeof PAGE_SECTIONS.FOOTERS } | null {
  if (group.component?.options?.groupType !== GROUP_LOOKUP.CHECKBOX_GROUP) return null
  if (!group.children?.length) return null
  const first = group.children[0]
  const firstPid = first.pageId ?? resolvePageIdForElementInPages(pages, first.elementId)
  if (!firstPid) return null
  if (
    !group.children.every(c => {
      const pid = c.pageId ?? resolvePageIdForElementInPages(pages, c.elementId)
      return pid === firstPid
    })
  ) {
    return null
  }
  const page = pages.find(p => p.id === firstPid)
  if (!page) return null
  for (const sectionType of [PAGE_SECTIONS.HEADERS, PAGE_SECTIONS.FOOTERS] as const) {
    const section = page.component?.options?.[sectionType]
    if (!section) continue
    const ids = collectElementIdsInSectionSubtree(section as IElement)
    if (group.children.every(c => ids.has(c.elementId))) {
      return { pageId: firstPid, sectionType }
    }
  }
  return null
}

/**
 * Finds the checkbox group on `targetPageId` that is the header/footer replica of `sourceGroup` on `sourcePageId`
 * (same logical footprint via `headerFooterCloneIdMap`).
 */
export function findHeaderFooterReplicaCheckboxGroupId(
  groups: GroupFields<GroupFieldsOptions>[],
  sourceGroup: GroupFields<GroupFieldsOptions>,
  sourcePageId: string,
  targetPageId: string,
  cloneIdMap: HeaderFooterCloneIdMap,
  pages: IPage[]
): string | undefined {
  if (sourcePageId === targetPageId) return sourceGroup.id
  const sourceToClone = buildHeaderFooterElementIdMapForTargetPage(sourceGroup.children, sourcePageId, targetPageId, cloneIdMap)
  if (!sourceToClone) return undefined
  const footprint = [...Object.values(sourceToClone)].sort().join('\0')
  const match = groups.find(g => {
    if (g.component?.options?.groupType !== GROUP_LOOKUP.CHECKBOX_GROUP) return false
    if (
      !g.children?.every(c => {
        const pid = c.pageId ?? resolvePageIdForElementInPages(pages, c.elementId)
        return pid === targetPageId
      })
    ) {
      return false
    }
    return [...g.children.map(c => c.elementId)].sort().join('\0') === footprint
  })
  return match?.id
}

/**
 * Resolve element id on `targetPageId` for header/footer replication using `headerFooterCloneIdMap`.
 * Supports host ids that are map keys (canonical) or clone ids for `hostPageId` (reverse lookup).
 */
export function resolveReplicatedSectionElementIdOnTargetPage(
  elementIdOnHost: string,
  hostPageId: string,
  targetPageId: string,
  cloneIdMap: HeaderFooterCloneIdMap
): string | undefined {
  if (hostPageId === targetPageId) return elementIdOnHost

  const direct = cloneIdMap[elementIdOnHost]?.[targetPageId]
  if (direct) return direct

  for (const sourceId of Object.keys(cloneIdMap)) {
    const per = cloneIdMap[sourceId]
    if (!per) continue
    if (per[hostPageId] === elementIdOnHost) {
      const onTarget = per[targetPageId]
      if (onTarget != null && onTarget !== '') return onTarget
      return sourceId
    }
  }
  return undefined
}

/**
 * Maps each group child's element id (on host page) to the corresponding id on `targetPageId`.
 * Returns null if any member cannot be resolved.
 */
export function buildHeaderFooterElementIdMapForTargetPage(
  groupChildren: { pageId: string; elementId: string }[],
  hostPageId: string,
  targetPageId: string,
  cloneIdMap: HeaderFooterCloneIdMap
): Record<string, string> | null {
  const map: Record<string, string> = {}
  for (const c of groupChildren) {
    const resolved = resolveReplicatedSectionElementIdOnTargetPage(c.elementId, hostPageId, targetPageId, cloneIdMap)
    if (!resolved) return null
    map[c.elementId] = resolved
  }
  return map
}

/** True if a checkbox group already exists on `targetPageId` with this exact set of element ids (load / idempotent sync). */
export function hasCheckboxGroupReplicaFootprintOnPage(
  groups: GroupFields<GroupFieldsOptions>[],
  targetPageId: string,
  sortedCloneElementIds: string[],
  pages: IPage[]
): boolean {
  const footprint = sortedCloneElementIds.join('\0')
  return groups.some(g => {
    if (g.component?.options?.groupType !== GROUP_LOOKUP.CHECKBOX_GROUP) return false
    if (
      !g.children?.every(c => {
        const pid = c.pageId ?? resolvePageIdForElementInPages(pages, c.elementId)
        return pid === targetPageId
      })
    ) {
      return false
    }
    return [...g.children.map(c => c.elementId)].sort().join('\0') === footprint
  })
}

/** Drops checkbox groups that are entirely on `targetPageId` and match this sorted element-id footprint (re-sync). */
export function filterOutCheckboxGroupReplicaFootprint(
  groups: GroupFields<GroupFieldsOptions>[],
  targetPageId: string,
  sortedExpectedCloneElementIds: string[],
  pages: IPage[]
): GroupFields<GroupFieldsOptions>[] {
  const footprint = sortedExpectedCloneElementIds.join('\0')
  return groups.filter(g => {
    if (g.component?.options?.groupType !== GROUP_LOOKUP.CHECKBOX_GROUP) return true
    if (
      !g.children?.every(c => {
        const pid = c.pageId ?? resolvePageIdForElementInPages(pages, c.elementId)
        return pid === targetPageId
      })
    ) {
      return true
    }
    const foot = [...g.children.map(c => c.elementId)].sort().join('\0')
    return foot !== footprint
  })
}

/**
 * Clone a checkbox group for another page's replicated header/footer (new group id, new fieldId, remapped children).
 * Same idea as `cloneGroupWithNewIds` plus `pageId` and fillable field id regeneration.
 */
export function cloneCheckboxGroupForReplicatedSectionPage(
  group: GroupFields<GroupFieldsOptions>,
  targetPageId: string,
  sourceElementIdToCloneId: Record<string, string>,
  currentGroups: GroupFields<GroupFieldsOptions>[]
): GroupFields<GroupFieldsOptions> {
  const data = cloneDeep(group)
  const groupType = data.component?.options?.groupType ?? GROUP_LOOKUP.CHECKBOX_GROUP
  return {
    ...data,
    id: uuidv4(),
    component: {
      ...data.component,
      options: {
        ...data.component.options,
        [ELEMENTS_META.FIELD_ID]: getNextIdForGroup(currentGroups, groupType),
      },
    },
    children: data.children.map(child => ({
      ...child,
      pageId: targetPageId,
      elementId: sourceElementIdToCloneId[child.elementId],
    })),
  }
}

/**
 * Clones a section (header or footer) for a target page. Fillable/floating elements
 * get stable clone IDs from cloneIdMap (or new IDs stored in the map). Non-fillable
 * elements get new UUIDs to avoid duplicate DOM ids. Mutates cloneIdMap.
 *
 * @param sourceSection - Section from source page (e.g. first page header)
 * @param targetPageId - ID of the page this clone is for
 * @param cloneIdMap - sourceElementId -> { targetPageId -> cloneId }; updated in place
 * @returns Cloned section with unique IDs; fillable elements use stable clone IDs from map
 */
export function cloneSectionWithStableFillableIds<T extends IElement<any>>(
  sourceSection: T,
  targetPageId: string,
  cloneIdMap: HeaderFooterCloneIdMap
): T {
  const clone = cloneDeep(sourceSection) as T

  function assignId(sourceEl: IElement, cloneEl: IElement) {
    if (sourceEl.id == null) return
    const isFillable = floatingElementsSet.has(sourceEl.type)
    const targetMap = cloneIdMap[sourceEl.id] ?? (cloneIdMap[sourceEl.id] = {})
    if (isFillable) {
      const existingCloneId = targetMap[targetPageId]
      if (existingCloneId) {
        cloneEl.id = existingCloneId
      } else if (cloneEl.id !== sourceEl.id) {
        // Already has a different id (e.g. from loaded document) – keep it and register
        targetMap[targetPageId] = cloneEl.id
      } else {
        const newId = uuidv4()
        cloneEl.id = newId
        targetMap[targetPageId] = newId
      }
    } else {
      // Block elements: keep stable per-page ids when re-cloning (margin/padding updates call this often).
      // Otherwise activeElementId points at a replica node that disappears every sync → properties drawer closes.
      const existingCloneId = targetMap[targetPageId]
      if (existingCloneId) {
        cloneEl.id = existingCloneId
      } else {
        const newId = uuidv4()
        cloneEl.id = newId
        targetMap[targetPageId] = newId
      }
    }
  }

  function traverse(sourceNodes: IElement[], cloneNodes: IElement[]) {
    for (let i = 0; i < sourceNodes.length; i++) {
      const sourceEl = sourceNodes[i]
      const cloneEl = cloneNodes[i]
      if (!sourceEl || !cloneEl) continue
      assignId(sourceEl, cloneEl)
      if (Array.isArray(sourceEl.children) && sourceEl.children.length && Array.isArray(cloneEl.children)) {
        traverse(sourceEl.children, cloneEl.children)
      }
    }
  }

  assignId(sourceSection as IElement, clone as IElement)
  traverse(sourceSection.children || [], clone.children || [])
  return clone
}

/**
 * Builds the header/footer clone ID map from existing pages (e.g. after loading a document).
 * For each page after the first, records which element ID on that page corresponds to which
 * source element ID on page 0, so we don't assign new IDs to elements that already have
 * per-page IDs when replicating later.
 *
 * @param pages - Loaded pages (page 0 is source for header/footer)
 * @returns Map to pass to cloneSectionWithStableFillableIds
 */
export function buildHeaderFooterCloneIdMapFromPages(pages: IPage[]): HeaderFooterCloneIdMap {
  const map: HeaderFooterCloneIdMap = {}
  if (pages.length === 0) return map

  const page0 = pages[0]
  const sourceHeader = page0?.component?.options?.headers
  const sourceFooter = page0?.component?.options?.footers

  function recordSection(sourceSection: IElement | undefined, sectionType: 'headers' | 'footers') {
    if (!sourceSection?.children?.length) return
    for (let pageIndex = 1; pageIndex < pages.length; pageIndex++) {
      const targetPage = pages[pageIndex]
      const targetSection = sectionType === 'headers' ? targetPage?.component?.options?.headers : targetPage?.component?.options?.footers
      if (!targetSection?.children?.length) continue
      const targetPageId = targetPage.id
      traverseAndRecord(sourceSection.children, targetSection.children, targetPageId)
    }
  }

  function traverseAndRecord(sourceNodes: IElement[], targetNodes: IElement[], targetPageId: string) {
    for (let i = 0; i < sourceNodes.length && i < targetNodes.length; i++) {
      const sourceEl = sourceNodes[i]
      const targetEl = targetNodes[i]
      if (!sourceEl?.id || !targetEl?.id) {
        if (Array.isArray(sourceEl?.children) && Array.isArray(targetEl?.children)) {
          traverseAndRecord(sourceEl.children, targetEl.children, targetPageId)
        }
        continue
      }
      // Always record target id per page (including when ids match) so later header/footer re-clones can reuse it.
      const targetMap = map[sourceEl.id] ?? (map[sourceEl.id] = {})
      targetMap[targetPageId] = targetEl.id
      if (Array.isArray(sourceEl.children) && Array.isArray(targetEl.children)) {
        traverseAndRecord(sourceEl.children, targetEl.children, targetPageId)
      }
    }
  }

  recordSection(sourceHeader as IElement | undefined, 'headers')
  recordSection(sourceFooter as IElement | undefined, 'footers')
  return map
}

/**
 * 2) Builds a lookup map { [id]: node } for every node having an `id`.
 */
export function flattenTreeToMap<T extends IElement<any>>(input: T | T[]): { result: T[]; map: Record<string, T> } {
  const result: T[] = []
  const map: Record<string, T> = {}
  const roots = Array.isArray(input) ? input : [input]

  function traverse(nodes: T[]) {
    for (const node of nodes) {
      if (node.id != null) {
        result.push(node)
        map[node.id] = node
      }
      if (Array.isArray(node.children) && node.children.length) {
        traverse(node.children as T[])
      }
    }
  }

  traverse(roots)
  return { result, map }
}

/**
 * Remaps the top‐level keys of `inputObj` according to `idMap`.
 *
 * For each [oldKey → value] in inputObj:
 *   • If idMap[oldKey] exists, use that as the new key.
 *   • Otherwise, keep the oldKey.
 *
 * @param {Record<string, any>} inputObj
 * @param {Record<string, string>} idMap
 * @returns {Record<string, any>}
 */
export const remapKeysForCVLinkage = (
  input: Record<string, ICustomFieldsLink>,
  idMap: Record<string, string>
): Record<string, ICustomFieldsLink> => {
  const inputObj = cloneDeep(input)
  const output: Record<string, any> = {}
  for (const [oldKey, value] of Object.entries(inputObj)) {
    // 1) clone the value so we don't mutate the original
    const v = cloneDeep(value)
    // 2) remove its contactId property
    delete v.contactId
    // 3) decide on the new output key
    const newKey = idMap[oldKey] ?? oldKey
    // 4) assign the modified object (with no contactId) into output
    output[newKey] = v
  }
  return output
}

/**
 * For every top‐level key (e.g. "24C7U2Yfwd67tU9kvWba"), look at its array of ICustomField.
 * If you ever see two entries with the same (id + fieldKey) pair, throw an Error.
 */
export const checkForDuplicatesFieldKeyInCustomValueLinkage = (buckets: Record<string, ICustomFieldsLink[]>, recipients: IRecipient[]) => {
  const recipientMap = new Map<string, IRecipient>()
  const errorMap = new Map<string, string>()
  recipients.forEach(recipient => {
    recipientMap.set(recipient.id, recipient)
  })

  for (const [recipientId, list] of Object.entries(buckets)) {
    const seen = new Set<string>()
    for (const entry of list) {
      const pairKey = `${entry.fieldKey}`
      if (seen.has(pairKey)) {
        const rec = recipientMap.get(recipientId)
        let contactName = 'Contact'
        if (rec) {
          if (rec.contactName) {
            contactName = rec.contactName
          } else if (rec.firstName && rec.lastName) {
            contactName = `${rec.firstName} ${rec.lastName}`
          }
        }

        if (!errorMap.has(`${recipientId}-${entry.fieldKey}`)) {
          const errorMessage = i18n.global.t('validations.invalid.duplicateCustomValueLinkageConfig', {
            contactName,
            fieldKey: entry.fieldKey,
          })
          errorMap.set(`${recipientId}-${entry.fieldKey}`, errorMessage)
        }
      }

      seen.add(pairKey)
    }
  }

  // get all the errors from the errorMap and return them as an array
  return Array.from(errorMap.values())
}

export const stripUnderscoreIds = (obj: any, removeKeys?: string[]): Record<string, any> => {
  if (removeKeys && removeKeys.length > 0) {
    for (const key of removeKeys) {
      if (obj[key]) {
        delete obj[key]
      }
    }
  }

  if (Array.isArray(obj)) {
    return obj.map(stripUnderscoreIds)
  }
  if (obj && typeof obj === 'object') {
    // Return empty object as is
    if (Object.keys(obj).length === 0) {
      return obj
    }
    const out: Record<string, any> = {}
    for (const [key, val] of Object.entries(obj)) {
      if (key === '_id') continue
      out[key] = stripUnderscoreIds(val)
    }
    return out
  }
  return obj
}

/**
 * Sanitizes pages for content library by removing headers and footers.
 * Headers and footers are out of scope for content library and should not be saved.
 *
 * @param {IElement[]} pages - Array of page elements to sanitize
 * @returns {IElement[]} - Array of sanitized page elements with headers/footers removed
 */
export function sanitizePagesForContentLibrary(pages: IElement[]): IElement[] {
  if (!pages || pages.length === 0) {
    return pages
  }

  return pages.map(page => {
    // Clone the page to avoid mutating the original
    const sanitizedPage = cloneDeep(page)

    // Remove headers if they exist
    if (sanitizedPage?.component?.options?.[PAGE_SECTIONS.HEADERS]) {
      delete sanitizedPage.component.options[PAGE_SECTIONS.HEADERS]
    }

    // Remove footers if they exist
    if (sanitizedPage?.component?.options?.[PAGE_SECTIONS.FOOTERS]) {
      delete sanitizedPage.component.options[PAGE_SECTIONS.FOOTERS]
    }

    return sanitizedPage
  })
}
